import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Link, Navigate } from "react-router-dom";
import { fmtDT, fmtINR, senderPublicStatus } from "../../lib/constants";
import { toast } from "sonner";
import { Plus, Package as PackageIcon } from "@phosphor-icons/react";
import { SavedQuotes } from "./SavedQuotes";
import { COMPANY_INACTIVE_MESSAGE, isCompanyInactive } from "../../components/RequireSenderCompany";

export default function SenderDashboard() {
  const { profile } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/shipments").then((r) => setShipments(r.data)).catch((e) => toast.error(e.displayMessage)).finally(() => setLoading(false));
  }, []);

  if (!profile) return null;
  if (profile.role !== "SENDER") return <Navigate to="/" replace />;
  const inactive = isCompanyInactive(profile);

  return (
    <div className="max-w-[1200px] mx-auto p-6">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="overline">SENDER PORTAL</div>
          <h1 className="heading text-3xl">My Shipments</h1>
          <div className="text-xs text-[hsl(var(--muted))] mt-1">
            Account: <span className="id-chip">{profile.public_sender_id}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/sender/concierge" className="btn-secondary" data-testid="sender-concierge-button">
            Concierge
          </Link>
          {!inactive && (
            <Link to="/sender/new" className="btn-primary" data-testid="new-booking-button">
              <Plus size={14} weight="bold" /> New Booking
            </Link>
          )}
        </div>
      </div>

      {inactive ? (
        <div className="mb-6 px-3 py-2 rounded bg-[hsl(var(--warning)/0.1)] border border-[hsl(var(--warning)/0.4)] text-xs" data-testid="company-inactive-note">
          <span className="font-semibold">Your company is currently inactive.</span> {COMPANY_INACTIVE_MESSAGE}
        </div>
      ) : <SavedQuotes />}

      <div className="grid-card grid-card-strong">
        {loading ? <div className="p-10 mono text-sm">Loading…</div> :
         shipments.length === 0 ? (
          <div className="p-10 text-center">
            <PackageIcon size={32} weight="duotone" className="mx-auto mb-3 opacity-50" />
            <div className="overline mb-1">NO SHIPMENTS YET</div>
            {!inactive && <Link to="/sender/new" className="text-sm underline">Create your first booking</Link>}
          </div>
        ) : (
          <table className="table-air12">
            <thead><tr><th>Shipment #</th><th>Route</th><th>Status</th><th>Charged</th><th>Payable</th><th>Created</th><th></th></tr></thead>
            <tbody>
              {shipments.map((s) => (
                <tr key={s.id} data-testid={`sender-shipment-${s.public_shipment_number}`}>
                  <td><Link to={`/sender/shipments/${s.id}`} className="id-chip id-chip-strong">{s.public_shipment_number}</Link></td>
                  <td><span className="id-chip">{s.route_code}</span></td>
                  <td><span className="text-sm" data-testid={`sender-status-${s.public_shipment_number}`}>{senderPublicStatus(s)}</span></td>
                  <td className="mono">{s.total_chargeable_weight_kg} kg</td>
                  <td className="mono">{fmtINR(s.total_payable)}</td>
                  <td className="text-xs">{fmtDT(s.created_at)}</td>
                  <td><Link to={`/sender/shipments/${s.id}`} className="text-xs underline font-semibold">Open →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
